import { type InputType, COMPANY_NAME_FC } from 'config'

export const purchaseAdviceAccounting: Record<string, InputType> = {
  BasePrice: {
    title: 'Base Price',
    inputType: 'text',
    prefix: '%',
    readOnly: true,
    span: 2,
  },
  LoanTerm: {
    title: 'Loan Term (months)',
    inputType: 'text',
    readOnly: true,
    span: 2,
  },
  ProductType: {
    title: 'Product Type',
    inputType: 'text',
    readOnly: true,
    span: 2,
  },
  OriginalBalance: {
    title: 'Original Balance',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    readOnly: true,
    span: 2,
  },
  NoteRate: {
    title: 'Note Rate',
    inputType: 'text',
    prefix: '%',
    readOnly: true,
    span: 2,
  },
  BuyRate: {
    title: 'Buy Rate',
    inputType: 'text',
    type: 'number',
    prefix: '%',
    span: 2,
  },
  PAUnpaidPrincipalBalance: {
    title: 'Current Balance',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  InterestSpread: {
    title: 'Interest Spread',
    inputType: 'text',
    prefix: '%',
    readOnly: true,
    span: 3,
  },
  InterestCalculation: {
    title: 'Interest Calculation',
    inputType: 'select',
    hasDefaultOption: true,
    options: {
      Dutch: 'Dutch',
      NonDutch: 'Non Dutch',
    },
    visible: false,
    span: 2,
  },
  PAPremiumOrDiscountCalc: {
    title: 'Premium(Discount) Calculated',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
    disabled: true,
  },
  // PAPremiumOrDiscountOV: {
  //   title: 'Premium(Discount) Override',
  //   inputType: 'text',
  //   type: 'thousandSep',
  //   prefix: '$',
  //   span: 2,
  // },
  PANetPrice: {
    title: 'Net Price',
    inputType: 'text',
    type: 'number',
    prefix: '%',
    span: 3,
  },
  PA_SRPAmountCalc: {
    title: 'Net SRP Calculated',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
    disabled: true,
  },
  PA_SRPPerc: {
    title: 'Net SRP',
    inputType: 'text',
    type: 'number',
    prefix: '%',
    span: 3,
  },
  PARollFee: {
    title: 'Lender Fee',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  PAOtherFees: {
    title: 'Other Fee (Flood Cert Fee)',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  PAEscrowBalance: {
    title: 'Escrow Balance to Investor',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  APiece: {
    title: 'A Piece',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  BPiece: {
    title: 'B Piece',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  PariPassu: {
    title: 'Pari Passu',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  ConstructionReserve: {
    title: 'Construction Reserve',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  InterestReserve: {
    title: 'Interest Reserve',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  DailyRateBasis: {
    title: 'Daily Rate Basis',
    inputType: 'select',
    options: {
      '360': '30/360',
      '365': '30/365',
      actual_360: 'Actual/360',
      actual_365: 'Actual/365',
    },
    hasDefaultOption: true,
    required: true,
    history: true,
    span: 3,
  },
  PAInterestAmount: {
    title: 'Interest Paid Amount',
    inputType: 'text',
    tooltip: `(('Current/Original Balance' * 'Buy Rate') / 100 / Daily Rate Year Days) * 'Interest Paid Days'`,
    type: 'thousandSep',
    prefix: '$',
    span: 3,
    disabled: true,
  },
  PAInterestDays: {
    title: 'Interest Paid Days',
    inputType: 'text',
    tooltip: `Days between 'Paid to Date' and 'Settlement Date' (Actual or 30-day months per Daily Rate Basis)`,
    type: 'number',
    span: 3,
    disabled: true,
  },
  PABuydownFunds: {
    title: 'Buydown',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  PreviouslySwept: {
    title: 'Sweep Holdback',
    inputType: 'text',
    type: 'thousandSep',
    prefix: '$',
    span: 3,
  },
  PAWireAmount: {
    title: 'Investor Wire Amount',
    inputType: 'text',
    tooltip: `'Current Balance' + <br/>'Premium(Discount) Calculated' + <br/>'Net SRP Calculated' + <br/>'Lender Fee' + <br/>'Other Fee (Flood Cert Fee)' + <br/>'Escrow Balance to Investor' + <br/>'A Piece' + <br/>'B Piece' + <br/>'Pari Passu' + <br/>'Construction Reserve' + <br/>'Interest Reserve' + <br/>'Interest Paid Amount' + <br/>'Buydown'`,
    type: 'thousandSep',
    prefix: '$',
    readOnly: true,
  },
}

export const purchaseAdviceGeneralInfos: Record<string, InputType> = {
  InvestorCode: {
    title: 'Lendor',
    inputType: 'select',
    options: [],
    sort: true,
    hasDefaultOption: true,
  },
  FirstPaymentDate: {
    title: 'First Payment Date on Loan',
    inputType: 'text',
    type: 'date',
  },
  PaidToDate: {
    title: 'Paid to Date',
    inputType: 'text',
    type: 'date',
  },
  NextPaymentDueDate: {
    title: 'Next Payment Due Date',
    inputType: 'text',
    type: 'date',
  },
  InvestorFirstPaymentDate: {
    title: 'First Payment Due to Investor',
    inputType: 'text',
    type: 'date',
  },
  SettlementDate: {
    title: 'Settlement Date',
    inputType: 'text',
    type: 'date',
  },
  ServiceTransferDate: {
    title: 'Service Transfer Date',
    inputType: 'text',
    type: 'date',
  },
  LoanPurchasedDate: {
    title: 'Purchased Date',
    inputType: 'text',
    type: 'date',
  },
}

export const purchaseAdviceWireInstructions: Record<string, InputType> = {
  PAWireInstAccountName: {
    title: 'Account Name',
    inputType: 'text',
    span: 3,
  },
  PAWireInstAccountNumber: {
    type: 'bankAccountNo',
    title: 'Account Number',
    inputType: 'text',
    span: 3,
  },
  PAWireInstAccountAddress: {
    title: 'Account Address',
    inputType: 'map',
    span: 6,
  },
  PAWireInstBankName: {
    title: 'Bank Name',
    inputType: 'text',
    span: 3,
  },
  PAWireInstBankRoutingNumber: {
    type: 'routingNo',
    title: 'Bank Routing Number',
    inputType: 'text',
    span: 3,
  },
}

export const defaultSendSignatureInfos: Record<string, InputType> = {
  companyName: {
    title: 'Company Name',
    inputType: 'text',
    required: true,
  },
  name: {
    title: 'Name',
    inputType: 'text',
    required: true,
  },
  email: {
    title: 'Email',
    inputType: 'text',
    type: 'email',
    required: true,
  },
}

export interface PurchaseAdviceSellerHistory {
  id: string
  companyName: string
  name: string
  email: string
  unsignedPdfKey: string
  signedPdfKey?: string
  requestedBy: string // email
  createdAt: number
  requestCancelled?: boolean
}

export const purchaseAdvicePostClosing: Record<string, InputType> = {
  [`First_Payment_Due_${COMPANY_NAME_FC}`]: {
    title: `First Payment Due ${COMPANY_NAME_FC}`,
    inputType: 'checkbox',
  },
}
